import { useLocation } from '@reach/router';
import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import React, { use, useEffect, useState } from 'react';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import 'src/styling/global.scss';

import { useTimedPromotionForClub } from 'src/components/common/promo/use-timed-promotion';
import ClubHeaderDesktop from 'src/components/layout/redesign/club-header-desktop';
import ClubHeaderMobile from 'src/components/layout/redesign/club-header-mobile';
import { ClubCmsContent } from 'src/content/club-content-consumer';
import ClubContentProvider from 'src/content/club-content-provider';
import { getClubInformation, shouldUseClubRedesignForClub } from 'src/content/club-look-ups-by-id';
import { ClubFinderPageDataProvider } from 'src/content/cms-provider/club-finder-clubs-cms-provider';
import GlobalContentProvider from 'src/content/global-content-provider';
import { SiteContext } from 'src/context/site-context';
import { IS_MEMBER_COOKIE } from 'src/cookies/is-member-cookie';
import useRegisterClubAsLastViewed from 'src/custom-hooks/use-register-club-as-last-viewed';
import { useToggle } from 'src/custom-hooks/use-toggle';
import { addVarToDataLayer } from 'src/helpers/analytics-helper';
import { clubBannerIsActive } from 'src/helpers/club-helper';
import { FEATURE_FLAG, getFeatureFlagAtClub } from 'src/helpers/feature-flag-helper';
import tawk from 'src/helpers/tawk';
import { clubFacilitiesMobileNavHeight, globalBannerHeight } from 'src/styling/constants';
import mq from 'src/styling/media-queries';

import { getFacilitiesAtClub } from '../facilities/facilities';

import * as styles from './club-page-layout.module.scss';
import Footer from './footer';
import Header from './header';
import MobileNav from './mobile-nav';
import { MAIN_CONTENT_ID } from './page-layout-constants';
import type { SnippetsProps } from './snippets';
import Snippets from './snippets';

const runningInBrowser = typeof window !== `undefined` && !navigator.userAgent.includes('jsdom');

type ClubPageLayoutProps = PropsWithChildren<{
  siteId: string;
  clubAndClubFinderCmsPageData: unknown;
  globalCmsPageData: unknown;
  snippets?: SnippetsProps;
  disablePromoRibbon?: boolean;
}>;

const ClubPageLayout = ({
  clubAndClubFinderCmsPageData,
  globalCmsPageData,
  ...props
}: ClubPageLayoutProps) => {
  useRegisterClubAsLastViewed(props.siteId);
  return (
    <GlobalContentProvider globalCmsPageData={globalCmsPageData}>
      <ClubContentProvider clubCmsPageData={clubAndClubFinderCmsPageData}>
        <ClubFinderPageDataProvider pageData={clubAndClubFinderCmsPageData}>
          <LayoutContent {...props} />
        </ClubFinderPageDataProvider>
      </ClubContentProvider>
    </GlobalContentProvider>
  );
};

type LayoutContentProps = Omit<
  ClubPageLayoutProps,
  'clubAndClubFinderCmsPageData' | 'globalCmsPageData'
>;

const LayoutContent: React.FC<LayoutContentProps> = ({
  snippets,
  disablePromoRibbon,
  children,
}) => {
  const { sharedContent, features, newFeatures, pages, navigationMenuContent } =
    use(ClubCmsContent);
  const { buildType, siteId } = use(SiteContext);
  const clubName = getClubInformation(siteId).clubName;

  const { showGlobalBanner } = useTimedPromotionForClub();
  const bannerIsActive = showGlobalBanner || clubBannerIsActive(sharedContent);

  const [mobileNavigationOpened, setMobileNavigationOpened, , hideMobileNavigation] =
    useToggle(false);
  const [clubFacilitiesMobileNavEnabled, setClubFacilitiesMobileNavEnabled] = useState(false);

  const shouldUseNewHeader = shouldUseClubRedesignForClub(siteId);

  const clubFacilitiesPresent =
    !shouldUseNewHeader &&
    getFacilitiesAtClub(
      sharedContent?.clubFacilities,
      pages,
      navigationMenuContent.navigationPagesToDisplay?.clubFacilitiesPages
    ).length > 0;

  useEffect(() => {
    addVarToDataLayer({ clubName: clubName });
    if (runningInBrowser) {
      addVarToDataLayer({
        memberType: IS_MEMBER_COOKIE.isSet() ? 'member' : 'nonMember',
      });
    }

    void getFeatureFlagAtClub(siteId, FEATURE_FLAG.ENABLE_CLUB_FACILITIES_MOBILE_NAV)
      .catch(() => false)
      .then((response) => {
        setClubFacilitiesMobileNavEnabled(response);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO why are the dependencies not exhaustive?
  }, []);

  const currentPath = useLocation();

  const enquireNowLink = sharedContent?.enquireNowButton?.enquireNowButtonLink;
  const shouldUseLiveChat = shouldUseClubRedesignForClub(siteId)
    ? newFeatures.clubPageLiveChat
    : features.clubPageLiveChat;

  const shouldUseABBYChat = shouldUseClubRedesignForClub(siteId)
    ? newFeatures.clubPageAbbyChat
    : features.clubPageAbbyChat;

  if (!shouldUseLiveChat && !(enquireNowLink && currentPath.pathname.includes(enquireNowLink))) {
    tawk.hideWidget();
  }

  return (
    <>
      <div className={styles.wrapper}>
        {shouldUseNewHeader ? (
          <>
            <ClubHeaderMobile disablePromoRibbon={disablePromoRibbon} />
            <ClubHeaderDesktop disablePromoRibbon={disablePromoRibbon} />
          </>
        ) : (
          <>
            <Header
              clubName={clubName}
              toggleMobileNav={() => setMobileNavigationOpened(!mobileNavigationOpened)}
              showingMobileNav={!!mobileNavigationOpened}
              enableClubFacilitiesMobileNav={clubFacilitiesMobileNavEnabled}
            />
            {bannerIsActive && (
              <div
                css={mq({
                  paddingBottom: [globalBannerHeight.mobile, globalBannerHeight.default],
                })}
              />
            )}
            <div className="spacer" style={{ paddingBottom: '4.5rem' }} />
            <div
              className="spacer-nav"
              css={mq.withSmallTablet({
                paddingBottom: '3.5rem',
                display: ['none', 'none', 'inline'],
              })}
            />
            <MobileNav
              clubName={clubName}
              closeNav={() => hideMobileNavigation()}
              mobileNavigationOpened={!!mobileNavigationOpened}
              bannerIsActive={!!bannerIsActive}
            />
          </>
        )}
        <main
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          className={classNames('page-scroll', styles.pageScroll)}
          aria-label="Main content"
          style={
            {
              '--page-scroll-top':
                clubFacilitiesPresent && clubFacilitiesMobileNavEnabled
                  ? clubFacilitiesMobileNavHeight
                  : 0,
            } as React.CSSProperties
          }
        >
          {children}
          <Footer />
        </main>
      </div>
      <Snippets
        {...snippets}
        chatEnabled={shouldUseLiveChat}
        buildType={buildType}
        abbyChatEnabled={shouldUseABBYChat}
      />
    </>
  );
};

export default ClubPageLayout;
